import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@finitless/shared/components";
import { useTranslation } from "react-i18next";

import { Icon } from "../../ui";
import Pagination from "../../ui/Data/Pagination";
import { useBusinessesForProducts } from "../../../hooks/useProducts";

const ProductsIndex = () => {
  const { t } = useTranslation("products");
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset page when search term actually changes (after debounce)
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const { data, isLoading, refetch } = useBusinessesForProducts({
    page,
    pageSize: 20,
    searchTerm,
  });

  const businesses = data?.businesses || [];
  const pagination = data?.pagination || {};

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
  };

  const handleRefresh = () => {
    refetch();
  };

  return (
    <div className="space-y-4">
      <PageHeader title={t("header.title")} subtitle={t("header.subtitle")} />

      {/* Search Filter */}
      <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          {/* Search Input */}
          <div className="w-full min-w-0 flex-1 sm:w-auto">
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Icon name="Search" className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={t("search.placeholder")}
                value={searchInput}
                onChange={handleSearchChange}
                className="h-10 w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 pr-10 pl-10 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              {searchInput && (
                <button
                  onClick={handleClearSearch}
                  className="absolute top-1/2 right-2 flex h-5 w-5 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full bg-gray-600 transition-colors hover:bg-gray-500"
                  type="button"
                >
                  <svg
                    className="h-3 w-3 text-gray-300 hover:text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Refresh Button */}
          <div className="w-full flex-shrink-0 sm:w-auto">
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="inline-flex h-10 w-full items-center justify-center rounded-lg border border-transparent bg-blue-600 px-3 py-2 text-sm leading-4 font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
              <svg
                className={`mr-1 h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {isLoading ? t("actions.refreshing") : t("actions.refresh")}
            </button>
          </div>
        </div>
      </div>

      <div className="">
        {isLoading ? (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 12 }).map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse rounded-lg border border-gray-800 bg-gray-900 p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-12 w-12 rounded-lg bg-gray-800" />
                    <div className="min-w-0 flex-1">
                      <div className="mb-2 h-4 w-40 rounded bg-gray-800" />
                      <div className="h-3 w-56 rounded bg-gray-800" />
                    </div>
                  </div>
                  <div className="mt-3 h-4 w-24 rounded bg-gray-800" />
                </div>
              ))}
            </div>

            <div className="mt-4">
              <div className="animate-pulse rounded-lg border border-gray-700 bg-gray-800 p-4">
                <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                  <div className="h-4 w-64 rounded bg-gray-700" />
                  <div className="h-8 w-48 rounded bg-gray-700" />
                </div>
              </div>
            </div>
          </>
        ) : businesses.length > 0 ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
            {businesses.map((b) => (
              <button
                key={b.slug || b.id}
                onClick={() => navigate(`/menu/products/${b.id}`)}
                className="group h-full rounded-lg border border-gray-800 bg-gray-900 p-4 text-left transition-colors hover:border-gray-700 hover:bg-gray-800"
              >
                <div className="flex min-w-0 items-start gap-3">
                  {b.logo ? (
                    <img
                      src={b.logo}
                      alt={b.name}
                      className="h-12 w-12 flex-shrink-0 rounded-lg object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-gray-800">
                      <Icon name="Store" className="h-6 w-6 text-gray-400" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-white">
                      {b.name}
                    </div>
                    <div className="truncate text-sm text-gray-400">
                      {b.address || b.city?.name || ""}
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex items-center text-xs text-gray-400">
                  <span className="mr-1">{t("actions.manage")}</span>
                  <Icon name="ChevronRight" className="h-4 w-4" />
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="py-16 text-center text-gray-400">
            {t("empty.noBusinesses")}
          </div>
        )}

        {pagination?.total_pages > 1 && (
          <div className="mt-4">
            <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
              <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                <div className="text-sm text-gray-400">
                  {(() => {
                    const pageSizeLocal = pagination?.page_size || 20;
                    const total = pagination?.total || 0;
                    const start = total ? (page - 1) * pageSizeLocal + 1 : 0;
                    const end = Math.min(page * pageSizeLocal, total);
                    return (
                      <>
                        {t("pagination.showing", {
                          from: start,
                          to: end,
                          total,
                        })}
                      </>
                    );
                  })()}
                </div>

                <Pagination
                  currentPage={page}
                  totalPages={pagination.total_pages}
                  onPageChange={setPage}
                  maxVisiblePages={5}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsIndex;
